import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ClipLoader } from "react-spinners";
import images from "../../utilities/images/images";
import {
  fetchPracticeSetQuestionList,
  fetchPracticeSetTestDetails,
} from "./mistakeBookApi";
import {
  fetchExamQuestionsListSuccess,
  setPaperId,
  setSelectedSetId,
  setTestDetails,
  setTestType,
} from "../../redux/exam/examSlice";
import HeaderInstruction from "../aits/components/Instruction/HeaderInstruction";
import "./PracticeSetInstructionPage.css";

const MISTAKE_BOOK_PAPER_TYPE = 22;

const PracticeSetInstructionPage = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const state = location.state || {};

  const practiceSet = state.practiceSet || {};
  const subjectName =
    state.subjectName ||
    state.subject?.name ||
    practiceSet.subject ||
    "Physics";
  const subjectId = state.subjectId || state.subject?.id || "";
  const year = state.year;
  const setId = practiceSet.setId ?? practiceSet.id ?? state.set_id;
  const selectedMode =
    state.selectedMode === "test" ? "test" : "practice";
  const gamificationOn =
    selectedMode === "practice" ? Boolean(state.gamificationOn) : false;

  const setTitle = useMemo(() => {
    const base =
      practiceSet.title ||
      `Set - ${practiceSet.setNumber || setId || ""}`.trim();
    const count = practiceSet.questionCount;
    return count ? `${base} (${count} Questions)` : base;
  }, [practiceSet, setId]);

  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [instruction, setInstruction] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadInstructions = async () => {
      if (!setId) {
        setError("Practice set id is missing.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const result = await fetchPracticeSetTestDetails({
          paper_type: MISTAKE_BOOK_PAPER_TYPE,
          set_id: setId,
          subject: subjectName,
        });
        if (!cancelled) {
          setInstruction(result.instruction || {});
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Unable to load instructions for this practice set."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadInstructions();
    return () => {
      cancelled = true;
    };
  }, [setId, subjectName]);

  const handleBack = () => {
    history.push("/practice/mistake-book/practice-sets", {
      year,
      subjectId,
      subjectName,
    });
  };

  const handleBeginTest = async () => {
    if (!setId || starting) return;

    setStarting(true);
    setError("");
    try {
      const result = await fetchPracticeSetQuestionList({
        paper_type: MISTAKE_BOOK_PAPER_TYPE,
        set_id: setId,
        subject: subjectName,
        practiceMode: selectedMode,
        gamificationOn,
      });

      if (!result.questions?.length) {
        throw new Error("No questions found for this practice set.");
      }

      const firstPaperId =
        result.questions.find((q) => q?.paper_id != null)?.paper_id ?? null;

      const payload = {
        paperType: MISTAKE_BOOK_PAPER_TYPE,
        setId,
        paperId: firstPaperId,
        practiceMode: selectedMode,
        gamificationOn,
        forceFreshStart: !practiceSet.isResume,
        isResume: Boolean(practiceSet.isResume),
        source: "mistake_book_practice_set",
        test_name: setTitle,
        subject: subjectName,
        subjectId,
        returnTo: "/practice/mistake-book/practice-sets",
      };

      dispatch(setSelectedSetId(setId));
      dispatch(setTestType({ id: MISTAKE_BOOK_PAPER_TYPE }));
      if (firstPaperId != null) {
        dispatch(setPaperId(firstPaperId));
      }
      dispatch(setTestDetails(payload));
      dispatch(
        fetchExamQuestionsListSuccess({
          status: 200,
          data: result.questions,
        })
      );

      history.push("/practice/mistake-book/test", {
        paperType: MISTAKE_BOOK_PAPER_TYPE,
        setId,
        paperId: firstPaperId,
        fromMistakeBookPracticeSet: true,
        returnTo: "/practice/mistake-book/practice-sets",
        subjectName,
        subjectId,
        subject: { id: subjectId, name: subjectName },
        year,
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to start this practice set right now."
      );
    } finally {
      setStarting(false);
    }
  };

  const inst = instruction || {};
  const durationValue = inst.duration_time || inst.duration_mins;
  const hideDuration = !durationValue;

  return (
    <div className="ps-instruction-page">
      <header className="ps-instruction-header">
        <button
          type="button"
          className="ps-instruction-header__back"
          onClick={handleBack}
          aria-label="Go back"
        >
          <img src={images.new_backarrow} alt="" />
        </button>
        <h1 className="ps-instruction-header__title">Test Instruction</h1>
        <div className="ps-instruction-header__spacer" aria-hidden="true" />
      </header>

      {loading ? (
        <div className="ps-instruction-loader">
          <ClipLoader size={40} color="#C61C26" />
        </div>
      ) : null}

      {error ? <p className="ps-instruction-error">{error}</p> : null}

      {!loading ? (
        <div className="ps-instruction-body">
          <HeaderInstruction
            name={inst.paper_name || setTitle}
            type={inst.paper_type || MISTAKE_BOOK_PAPER_TYPE}
            duration={durationValue}
            totalQuestions={
              inst.total_questions || practiceSet.questionCount || "-"
            }
            maxMarks={inst.max_marks}
            date={inst.start_date}
            duration_icon={inst.duration_icon}
            paper_icon={inst.paper_icon}
            question_icon={inst.question_icon}
            hideDuration={hideDuration}
          />

          <div className="ps-instruction-actions">
            <button
              type="button"
              className="ps-instruction-actions__back"
              onClick={handleBack}
              disabled={starting}
            >
              Back
            </button>
            <button
              type="button"
              className="ps-instruction-actions__begin"
              onClick={handleBeginTest}
              disabled={starting}
            >
              <span>{starting ? "Starting..." : "Begin your Test"}</span>
              <img src={images.rightArrow_icon} alt="" />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PracticeSetInstructionPage;
